import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { capitalizeWords, getCurrentDate } from '../../utils/format';
import { getRadientBG } from '../../utils/display';
import Header from '../../core/Header';
import useGetCourses from '../courses/services/use-get-courses';

const HomePage: FC = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const { data: program } = useGetCourses(getCurrentDate());
  const flatCourses = program?.reunions?.flatMap(r => r.courses.flatMap(c => c)) || [];
  const quinteOfTheDay = flatCourses.find(course => course.paris.map(p => p.typePari).includes("QUINTE_PLUS"))

  const handleCoursesClick = () => {
    navigate(`courses/${getCurrentDate()}`);
  };

  const handleQuinteClick = () => {
    navigate(`courses/${getCurrentDate()}/pronos/${quinteOfTheDay?.numReunion}/${quinteOfTheDay?.numOrdre}`);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getRadientBG(),
        display: 'flex',
        flexDirection: 'column',
        color: '#f1f5f9',
      }}
    >
      <Header />

      {/* ── HERO ── */}
      <Box
        component="section"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            maxWidth: 1120,
            mx: 'auto',
            px: 2,
            py: { xs: 8, md: 12 },
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {/* Pill badge */}
          <Box
            sx={{
              display: 'inline-block',
              background: 'rgba(16,185,129,0.10)',
              border: '1px solid rgba(52,211,153,0.20)',
              borderRadius: '999px',
              px: 1.5,
              py: 0.5,
              fontSize: '0.72rem',
              color: '#6ee7b7',
              mb: 2.5,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              fontWeight: 500,
            }}
          >
            Analyse hippique · PMU · Quinté+
          </Box>

          {/* H1 */}
          <Typography
            component="h1"
            sx={{
              fontSize: isMobile ? '2rem' : '3.5rem',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              mb: 2,
            }}
          >
            Courses hippiques,{' '}
            <Box component="span" sx={{ color: '#34d399' }}>
              pronostics
            </Box>{' '}
            et{' '}
            <Box component="span" sx={{ color: '#34d399' }}>
              statistiques
            </Box>
          </Typography>

          {/* Hidden SEO paragraphs (preserved from original) */}
          <Typography component="p" sx={{ display: 'none' }}>
            Expert Turf est une plateforme spécialisée dans les pronostics
            hippiques. Grâce à une analyse algorithmique des performances, de la
            ferrure, des gains et des statistiques, nous proposons des
            prévisions fiables pour les courses PMU et les turfistes passionnés.
          </Typography>
          <Box sx={{ display: 'none' }}>
            <Typography component="h2">
              Chaque jour, consultez les courses du jour, les résultats passés,
              les statistiques détaillées et les pronostics pour maximiser votre
              expérience de pari hippique.
            </Typography>
          </Box>

          {/* Subtitle */}
          <Typography
            sx={{
              fontSize: isMobile ? '0.95rem' : '1.05rem',
              color: 'rgba(203,213,225,0.80)',
              maxWidth: 560,
              mx: 'auto',
              mb: 4,
              lineHeight: 1.65,
            }}
          >
            Consultez les courses du jour, explorez les résultats passés et
            analysez les statistiques détaillées — avec un focus quotidien sur
            le{' '}
            <Box component="strong" sx={{ color: '#e2e8f0' }}>
              Quinté+
            </Box>
          </Typography>

          {/* CTA buttons */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1.5,
            }}
          >
            <Button
              onClick={handleCoursesClick}
              sx={{
                width: "200px",
                background: '#10b981',
                color: 'secondary.contrastText',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 3,
                py: 1.2,
                borderRadius: '12px',
                textTransform: 'none',
                '&:hover': { background: '#34d399' },
              }}
            >
              Accéder aux courses
            </Button>

            <Button
              onClick={handleQuinteClick}
              disabled={!quinteOfTheDay}
              sx={{
                width: "200px",
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: '#e2e8f0',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 3,
                py: 1.2,
                borderRadius: '12px',
                textTransform: 'none',
                '&:hover': { background: 'rgba(255,255,255,0.10)' },
              }}
            >
              Quinté+ du jour
            </Button>

            <Button
              component="a"
              href="https://www.youtube.com/@ExpertTurf"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                width: "200px",
                background: 'rgba(239,68,68,0.10)',
                border: '1px solid rgba(248,113,113,0.25)',
                color: '#fecaca',
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 3,
                py: 1.2,
                borderRadius: '12px',
                textTransform: 'none',
                '&:hover': { background: 'rgba(239,68,68,0.18)' },
              }}
            >
              Chaîne YouTube
            </Button>
          </Box>

          {/* Stat cards row */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: 1.5,
              mt: 5,
            }}
          >
            {[
              {
                value: 'Quinté+',
                label: capitalizeWords(quinteOfTheDay?.hippodrome.libelleCourt || ""),
                color: 'secondary.main',
              },
              { value: 'PMU', label: `${flatCourses.length} courses`, color: '#60a5fa' },
              {
                value: 'Stats',
                label: 'Analyse détaillée',
                color: 'white',
              },
            ].map(({ value, label, color }) => (
              <Box
                key={value}
                sx={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)',
                  borderRadius: '16px',
                  px: 3,
                  py: 2,
                  minWidth: 130,
                  textAlign: 'center',
                }}
              >
                <Typography
                  color={color}
                  sx={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    lineHeight: 1,
                  }}
                >
                  {value}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.72rem',
                    color: 'rgba(203,213,225,0.55)',
                    mt: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 120, // ou "100%" si le parent a une largeur fixe
                    mx: 'auto',
                  }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          px: 2,
          pb: 2,
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          mt: 4,
        }}
      >
        {[
          'Les informations, analyses et pronostics proposés sur ce site sont fournis à titre strictement informatif. Ils ne constituent en aucun cas une garantie de gain ni un conseil financier personnalisé.',
          'Les paris hippiques comportent des risques de pertes financières. Pariez uniquement des sommes que vous pouvez vous permettre de perdre.',
          'Jeu responsable — Interdit aux mineurs de moins de 18 ans.',
          "Ce site ne propose pas de paris en ligne et n'est affilié à aucun opérateur de jeux d'argent.",
        ].map((text, i) => (
          <Typography
            key={i}
            sx={{
              fontSize: '0.62rem',
              color: 'rgba(226,232,240,0.40)',
              lineHeight: 1.5,
              mb: 0.5,
            }}
          >
            {text}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export default HomePage;
